import React from 'react';
import ReactDOM from 'react-dom/client';
import { App } from './App';
import './index.css';

// WebView2 (the engine inside Tauri on Windows) rejects calls to
// `crypto.subtle.digest` when the input is a Uint8Array view with a
// non-zero byteOffset, throwing "illegal non-contiguous size". Supabase's
// PKCE flow hits this every signup/login because gotrue-js encodes the
// password into a sliced TypedArray. We patch digest() to copy the bytes
// into a fresh contiguous buffer first — invisible to chrome/firefox,
// makes WebView2 happy. Same fix recommended in supabase-js issue #1083.
if (typeof crypto !== 'undefined' && crypto.subtle && '__TAURI_INTERNALS__' in window) {
  const orig = crypto.subtle.digest.bind(crypto.subtle);
  crypto.subtle.digest = ((algorithm: AlgorithmIdentifier, data: BufferSource) => {
    if (ArrayBuffer.isView(data)) {
      const view = data as ArrayBufferView;
      // Copy into a tight ArrayBuffer with no offset, no shared backing.
      const fresh = new Uint8Array(view.byteLength);
      fresh.set(new Uint8Array(view.buffer, view.byteOffset, view.byteLength));
      return orig(algorithm, fresh);
    }
    return orig(algorithm, data);
  }) as typeof crypto.subtle.digest;
}

// Recover from stale chunk references after a fresh deploy: when a lazy-
// loaded module fails because its hashed filename is gone (Cloudflare served
// the new index.html with new chunk names), force a one-shot reload so the
// user picks up the new build instead of seeing a MIME error.
const RELOAD_FLAG = 'erp_chunk_reloaded_at';
const shouldReload = () => {
  // Only auto-reload once per minute to avoid reload loops in the unlikely
  // case that the chunks really are missing server-side.
  const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? '0');
  return Date.now() - last > 60_000;
};
const markReloaded = () => {
  sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
};

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  if (shouldReload()) {
    markReloaded();
    window.location.reload();
  }
});

window.addEventListener('error', (event) => {
  const msg = event.message || '';
  if (
    /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(msg)
  ) {
    if (shouldReload()) {
      markReloaded();
      window.location.reload();
    }
  }
});

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
